/**
 * The global filter bar. Sixteen controls plus reset, in the spec's order, grouped
 * into three labelled bands: Claims, People, Period. The reader chooses which of the
 * sixteen the bar shows ("Choose filters" on the scope row); a hidden filter keeps
 * its value and still applies, and its scope pill stays, so nothing applies unseen.
 *
 * Every control is GLOBAL — it follows you from page to page — except Employee on
 * Supervisors and the Scorecard, where it is page-level (`EMPLOYEE_PAGE_SCOPED`): one
 * set on another page is shown on the scope row, with Apply here and a clear, and not
 * applied. The pages' own controls (the Overview's slicer, Claim detail's dropdowns,
 * Facilities' group and search…) are page-level, and live on the page, not here.
 *
 * Service type and Vendor used to sit here DISABLED with a reason on hover: on the
 * workbook exports the only service-line column was a payroll cost centre that read
 * "RCM" for almost everyone, and the only vendor column was a 65-row contractor
 * roster. Both are real columns in the curated model — `DimClaimType` carries claim
 * type and service type at the claim grain, and `DimEmployee` carries Paylocity's
 * vendor and work location on the person — so as of 2026-09-22 they are live, and
 * Office location joins them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { managementDefaultFor } from './ManagementToggle'
import { useAppData } from '@/data/AppData'
import {
  useFilters,
  ALL,
  EMPTY_FILTERS,
  MULTI_KEYS,
  FILTER_LABELS,
  MODELED_KEYS,
  TENURE_BANDS,
  PAYROLL_BANDS,
  hiddenByStatus,
  SLICE_NOTE,
  GROUP_NOTE,
} from '@/data/filters'
import { facilityGroupOptions } from '@/data/filterStorage'
import { DIMENSION_HELP, EMPLOYEE_THIS_PAGE, type Definition } from '@/measures/definitions'
import type { Filters, MultiKey } from '@/data/filters'
import { Pill } from './Bits'
import { MultiSelect } from './MultiSelect'
import { SearchPick } from './SearchPick'
import { MonthRangePicker, rangeSummary } from './MonthRangePicker'
import { useMediaQuery } from '@/ui/useMediaQuery'
import { IconFilterOff } from '@tabler/icons-react'
import { VIEW_KEY } from '@/state/sharedView'
import { usePreferences } from '@/ui/preferences'
import { int, monthSpan } from '@/format'
import { usePageLimit } from '@/access/pageLimit'

/*
 * The sixteen controls, by band, in the spec's order. `keys` are the filter fields a
 * control owns — the month range owns two — used to tell whether it is active and to
 * name it in "Choose filters". The chooser stores the HIDDEN ids, so a control added
 * here later shows by default.
 */
type ControlId =
  | MultiKey
  | 'supervisor'
  | 'employee'
  | 'management'
  | 'employmentStatus'
  | 'tenure'
  | 'payrollBand'
  | 'monthRange'

interface ControlDef {
  id: ControlId
  label: string
  keys: (keyof Filters)[]
}

const BANDS: { label: string; controls: ControlDef[] }[] = [
  {
    label: 'Claims',
    controls: [
      { id: 'claimType', label: 'Claim type', keys: ['claimType'] },
      { id: 'serviceType', label: 'Service type', keys: ['serviceType'] },
      { id: 'claimStatus', label: 'Claim status', keys: ['claimStatus'] },
      // Asana 1218824501473012: at the top, beside the claim filters, since a facility
      // is a claim's. Searchable like every list.
      { id: 'facilityGroup', label: 'Facility group', keys: ['facilityGroup'] },
    ],
  },
  {
    label: 'People',
    controls: [
      { id: 'department', label: 'Department', keys: ['department'] },
      { id: 'jobTitle', label: 'Job title', keys: ['jobTitle'] },
      { id: 'supervisor', label: 'Supervisor', keys: ['supervisor'] },
      { id: 'employee', label: 'Employee', keys: ['employee'] },
      { id: 'shore', label: 'Is on shore', keys: ['shore'] },
      { id: 'vendor', label: 'Vendor', keys: ['vendor'] },
      { id: 'officeLocation', label: 'Office location', keys: ['officeLocation'] },
      { id: 'employmentStatus', label: 'Employee status', keys: ['employmentStatus'] },
      { id: 'tenure', label: 'Tenure', keys: ['tenure'] },
      { id: 'payrollBand', label: 'Payroll range', keys: ['payrollBand'] },
    ],
  },
  {
    label: 'Period',
    controls: [{ id: 'monthRange', label: 'Month date range', keys: ['monthFrom', 'monthTo'] }],
  },
]

const ALL_CONTROL_IDS = BANDS.flatMap((b) => b.controls.map((c) => c.id))

const isSet = (f: Filters, k: keyof Filters) => {
  const v = f[k]
  return Array.isArray(v) ? v.length > 0 : v !== EMPTY_FILTERS[k]
}

const MODELED_SET = new Set<string>(MODELED_KEYS)

/**
 * Below the disclosure breakpoint the label sits above the controls, since a side
 * column would only steal width from a single stacked control.
 */
const LABEL_COL = 96

/**
 * One band: its name in a fixed column to the left, on the controls' own label line,
 * so each band has a single caps row. Every band's grid spans the same width with the
 * same column recipe, so the columns line up from band to band. `auto-fill` rather
 * than `auto-fit` so a band with only one or two controls keeps them at one column
 * each instead of stretching them across the bar.
 */
function Band({
  label,
  narrow,
  children,
}: {
  label: string
  narrow: boolean
  children: ReactNode
}) {
  return (
    <section
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: narrow ? 'column' : 'row',
        alignItems: 'flex-start',
        gap: narrow ? 4 : 10,
      }}
    >
      <span
        className="eyebrow"
        style={{ flex: 'none', width: narrow ? 'auto' : LABEL_COL }}
        aria-hidden="true"
      >
        {label}
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          alignSelf: 'stretch',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 158px), 1fr))',
          gap: 10,
        }}
      >
        {children}
      </div>
    </section>
  )
}

/** A text-only action inside the chooser, styled as a link. */
const linkBtn: React.CSSProperties = {
  border: 0,
  background: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  fontSize: 'var(--t-micro)',
  color: 'var(--accent)',
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
}

/**
 * "Choose filters": a popover of the sixteen controls as checkboxes, grouped by band.
 * Unchecked controls do not render in the bar. Active ones are marked so nobody hides
 * a filter without knowing it is on.
 */
function FilterChooser({
  hidden,
  onChange,
  filters,
}: {
  hidden: string[]
  onChange: (hidden: string[]) => void
  filters: Filters
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const hiddenSet = new Set(hidden)
  const active = (c: ControlDef) => c.keys.some((k) => isSet(filters, k))
  const toggle = (id: ControlId) =>
    onChange(hiddenSet.has(id) ? hidden.filter((h) => h !== id) : [...hidden, id])

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Choose filters
        {hidden.length > 0 && (
          <span style={{ color: 'var(--ink-3)' }}> · {hidden.length} hidden</span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose filters"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 30,
            marginTop: 4,
            minWidth: 232,
            background: 'var(--surface)',
            border: '1px solid var(--rule-2)',
            borderRadius: 7,
            boxShadow: 'var(--shadow)',
            padding: '10px 12px',
            display: 'grid',
            gap: 8,
            fontSize: 'var(--t-table)',
          }}
        >
          {/*
            Shayna, review #110 (2026-10-06), on the old "Show all · Show only active":
            "is this meant to be select/deselect all?" It is: those two ticked every box,
            and unticked every box but the filters in use. Named for what they do now, with
            a line saying the boxes choose controls, not values.
          */}
          <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', whiteSpace: 'normal', maxWidth: 232 }}>
            Tick the filters to show in the bar. Hiding one never changes what is filtered.
          </span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <button type="button" style={linkBtn} onClick={() => onChange([])}>
              Select all
            </button>
            <button
              type="button"
              style={linkBtn}
              title="Unticks every filter except the ones in use, which stay in the bar so nothing filters out of sight"
              onClick={() =>
                onChange(
                  BANDS.flatMap((b) => b.controls)
                    .filter((c) => !active(c))
                    .map((c) => c.id),
                )
              }
            >
              Deselect all
            </button>
            <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
              (filters in use stay)
            </span>
          </div>
          {BANDS.map((b) => (
            <div key={b.label} style={{ display: 'grid', gap: 3 }}>
              <span className="eyebrow">{b.label}</span>
              {b.controls.map((c) => (
                <label
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenSet.has(c.id)}
                    onChange={() => toggle(c.id)}
                    style={{ margin: 0 }}
                  />
                  {c.label}
                  {active(c) && (
                    <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
                      · active
                    </span>
                  )}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Query params that say WHERE a page is, not what it is filtered to, so Clear filters
 * leaves them: the facility or group Facilities has open, and an open review thread.
 */
const KEEP_ON_CLEAR: Record<string, readonly string[]> = {
  '/facilities': ['facility', 'fgroup'],
}
const KEEP_EVERYWHERE = ['comment']

/**
 * Clear filters, everywhere it appears (the header, the filter bar): every filter back
 * to empty, the page's own picks and personal state with them (the reset clears the
 * whole entry: docs/STATE.md), the Management switch to this tab's default. Where the
 * page is (`KEEP_ON_CLEAR`) stays.
 *
 * `applied` counts the filter bar's filters; `dirty` is whether there is anything at
 * all to clear, page state included, so the button is never greyed out over a page
 * whose own picks are set.
 */
export function useClearAll(): { clearAll: () => void; applied: number; dirty: boolean } {
  const { filters, set, reset, activeCount, carriedEmployee } = useFilters()
  const { pathname, search, state } = useLocation()
  const keep = useMemo(() => [...KEEP_EVERYWHERE, ...(KEEP_ON_CLEAR[pathname] ?? [])], [pathname])
  const management = managementDefaultFor(pathname)
  const clearAll = useCallback(() => {
    reset(keep)
    set('management', management)
  }, [reset, set, keep, management])
  // Management is the page's own switch now (ManagementToggle), not a filter: it does
  // not count as one.
  const applied = activeCount - (filters.management !== 'all' ? 1 : 0)
  const pagePicks = [...new URLSearchParams(search).keys()].some(
    (k) => !k.startsWith('f.') && !keep.includes(k),
  )
  const personal = Object.keys(
    ((state as Record<string, unknown> | null)?.[VIEW_KEY] as object | undefined) ?? {},
  ).length > 0
  // An Employee carried in and not applied here is still set, and Clear clears it.
  const dirty =
    applied > 0 || pagePicks || personal || filters.management !== management || carriedEmployee !== null
  return { clearAll, applied, dirty }
}

/** What a page where Employee is page-level is called, on the scope row's note. */
const PAGE_NAME: Record<string, string> = { '/supervisors': 'Supervisors', '/scorecard': 'The Scorecard' }

/**
 * The header's Clear filters, on every page with a filter bar: the same reset as the
 * bar's own button, in reach without scrolling to it or opening a hidden bar.
 */
export function ClearFiltersButton() {
  const { clearAll, dirty } = useClearAll()
  const wide = useMediaQuery('(min-width: 900px)')
  return (
    <button
      type="button"
      className="btn"
      onClick={clearAll}
      disabled={!dirty}
      title={
        dirty
          ? 'Clear the filter bar and this page’s own picks, back to how the page opens'
          : 'Nothing to clear: no filters or picks are set'
      }
      aria-label="Clear filters"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...(wide ? {} : { padding: '5px 7px' }) }}
    >
      <IconFilterOff size={15} stroke={1.6} aria-hidden />
      {wide && 'Clear filters'}
    </button>
  )
}

export function FilterBar() {
  const { derived, scope, openMonths, payrollPending, closedThrough, claimFilterNote } = useAppData()
  const { filters, set, toggle, clear, carriedEmployee, applyCarriedEmployee } = useFilters()
  const { clearAll: reset, applied, dirty } = useClearAll()
  const { pathname } = useLocation()
  const groupOptions = useMemo(() => facilityGroupOptions(derived.facilityGroups), [derived.facilityGroups])

  /*
   * Below this width the controls stack one per row, and you scroll past the whole
   * bar before reaching any content. Collapse it behind a disclosure there; the
   * scope line stays visible, so what is filtered is never hidden — only the controls.
   */
  const narrow = useMediaQuery('(max-width: 900px)')
  const [expanded, setExpanded] = useState(false)
  // On desktop the same choice is a remembered preference — "Hide filters" on the
  // scope row — so a reader who knows their scope gets the room back on every visit.
  const { filtersOpen, setFiltersOpen, hiddenFilters, setHiddenFilters } = usePreferences()
  const showControls = narrow ? expanded : filtersOpen

  const hidden = new Set(hiddenFilters)
  // A page-limited reader is served no pay (api/shared/pages.ts), so no payroll range.
  const limited = usePageLimit() !== null
  const shown = (id: ControlId) => !hidden.has(id) && !(limited && id === 'payrollBand')

  /** Both ends at once, so a range is never half-applied between two renders. */
  const setMonths = (from: string, to: string) => {
    set('monthFrom', from)
    set('monthTo', to)
  }
  const monthsSet = filters.monthFrom !== ALL || filters.monthTo !== ALL

  const picked = filters.employee !== ALL ? derived.byName.get(filters.employee) : undefined
  const pickedHidden = picked && hiddenByStatus(filters, picked) ? picked : null

  const supervisorOptions = useMemo(
    () => derived.supervisors.map((v) => ({ value: v, label: v })),
    [derived.supervisors],
  )
  const employeeOptions = useMemo(
    () =>
      // The Paylocity ID rides along, so it can be typed in place of a name (#373).
      derived.people
        .map((p) => ({ value: p.employee, label: p.employee, detail: p.paylocityId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [derived.people],
  )

  /** Every multi-select is wired the same way; only its option list differs. */
  const multi = (
    key: MultiKey,
    options: string[],
    extra?: { title?: string; help?: Definition; modeled?: boolean; modeledNote?: string },
  ) => (
    <MultiSelect
      key={key}
      label={FILTER_LABELS[key]}
      options={options}
      value={filters[key]}
      onToggle={(v) => toggle(key, v)}
      onSet={(vs) => set(key, vs)}
      // The claim filters' M only while they still model something on this bundle;
      // the hover names it (data/claimCells.ts).
      modeled={extra?.modeled ?? (MODELED_SET.has(key) && claimFilterNote !== null)}
      modeledNote={extra?.modeledNote ?? claimFilterNote ?? undefined}
      title={extra?.title}
      help={extra?.help}
      // Every list types-to-search, however short (review #403).
      searchable
    />
  )

  /** Each control by id, so the bands can render only the ones the reader has kept. */
  const control: Record<ControlId, ReactNode> = {
    claimType: multi('claimType', derived.claimTypes, { help: DIMENSION_HELP.claimType }),
    serviceType: multi('serviceType', derived.serviceTypes, { help: DIMENSION_HELP.serviceType }),
    claimStatus: multi('claimStatus', derived.claimStatuses),
    // Its M while it scales anyone: where the rows carry no facility (GROUP_NOTE).
    facilityGroup: multi('facilityGroup', groupOptions, {
      help: DIMENSION_HELP.facilityGroup,
      modeled: scope.modeledNote === GROUP_NOTE,
      modeledNote: GROUP_NOTE,
    }),
    department: multi('department', derived.departments),
    jobTitle: multi('jobTitle', derived.jobTitles),
    // Searchable, as Job title is — Shayna, 2026-10-05 (#65). Still one pick each: the
    // control changed, not what the filter means.
    supervisor: (
      <SearchPick
        key="supervisor"
        label="Supervisor"
        value={filters.supervisor}
        allValue={ALL}
        allLabel="All supervisors"
        options={supervisorOptions}
        onChange={(v) => set('supervisor', v)}
      />
    ),
    employee: (
      <SearchPick
        key="employee"
        label="Employee"
        hint={PAGE_NAME[pathname] ? 'this page' : undefined}
        help={PAGE_NAME[pathname] ? EMPLOYEE_THIS_PAGE : undefined}
        value={filters.employee}
        allValue={ALL}
        allLabel="All employees"
        options={employeeOptions}
        onChange={(v) => set('employee', v)}
      />
    ),
    // Not a band control any more: it sits at the head of the scope row, which never
    // hides, because which people a figure covers is part of reading it.
    management: null,
    // No M badge: shore is an observed attribute of the employee, so this selects
    // people exactly. See data/filters.tsx.
    shore: multi('shore', derived.shores, {
      title: 'Shore is an attribute of the employee, not of the claim — this selects people.',
    }),
    vendor: multi('vendor', derived.vendors, {
      title:
        "Paylocity's vendor field. “Direct” is FCC payroll — the largest group, not a missing value.",
    }),
    officeLocation: multi('officeLocation', derived.officeLocations, {
      title: "Paylocity's work location.",
    }),
    employmentStatus: (
      <SearchPick
        key="employmentStatus"
        label="Employee status"
        value={filters.employmentStatus}
        allValue="both"
        allLabel="Active and terminated"
        options={[
          { value: 'active', label: 'Active only' },
          { value: 'terminated', label: 'Terminated only' },
        ]}
        onChange={(v) => set('employmentStatus', v as Filters['employmentStatus'])}
      />
    ),
    tenure: (
      <SearchPick
        key="tenure"
        label="Tenure"
        value={filters.tenure}
        allValue={ALL}
        allLabel="All tenures"
        options={[
          ...TENURE_BANDS.map((b, i) => ({ value: String(i), label: b.label })),
        ]}
        onChange={(v) => set('tenure', v)}
      />
    ),
    payrollBand: (
      <SearchPick
        key="payrollBand"
        label="Payroll range"
        hint="period"
        title={`Payroll with benefit load across the whole ${monthSpan(derived.allMonths)} period, not annualised — the month range does not change it`}
        value={filters.payrollBand}
        allValue={ALL}
        allLabel="All payroll"
        options={[
          ...PAYROLL_BANDS.map((b, i) => ({ value: String(i), label: b.label })),
        ]}
        onChange={(v) => set('payrollBand', v)}
      />
    ),
    // One control rather than two, per Shayna — it is a range, and two separate
    // "Month from" / "Month to" boxes do not read as one.
    monthRange: (
      <MonthRangePicker
        key="monthRange"
        months={derived.allMonths}
        openMonths={openMonths}
        payrollPending={payrollPending}
        closedThrough={closedThrough}
        from={filters.monthFrom}
        to={filters.monthTo}
        onChange={setMonths}
      />
    ),
  }

  const resetButton = (
    <label key="reset" style={{ display: 'grid', gap: 4 }}>
      <span className="eyebrow">&nbsp;</span>
      <button
        onClick={reset}
        disabled={!dirty}
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 'var(--t-table)',
          padding: '5px 7px',
          borderRadius: 6,
          border: '1px solid var(--rule-2)',
          background: 'var(--surface)',
          color: !dirty ? 'var(--ink-3)' : 'var(--ink)',
          cursor: !dirty ? 'default' : 'pointer',
        }}
      >
        Clear all filters
      </button>
    </label>
  )

  // A band with nothing left to show does not render. Reset always does, at the end of
  // the last band that is showing — even when every control has been hidden.
  const visibleBands = BANDS.map((b) => ({
    label: b.label,
    ids: b.controls.map((c) => c.id).filter(shown),
  })).filter((b) => b.ids.length > 0)
  const lastBand = visibleBands.length - 1

  return (
    <div style={{ background: 'var(--surface)' }}>
      <div className="page" style={{ padding: '12px 24px' }}>
        {narrow && (
          <button
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              background: 'var(--surface)',
              border: '1px solid var(--rule-2)',
              borderRadius: 7,
              padding: '8px 11px',
              marginBottom: showControls ? 12 : 0,
              cursor: 'pointer',
              color: 'var(--ink)',
              fontSize: 'var(--t-body)',
            }}
          >
            <span>Filters</span>
            {applied > 0 && <span className="pill">{applied} active</span>}
            <span
              className="mono"
              style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}
              aria-hidden="true"
            >
              {expanded ? '▴' : '▾'}
            </span>
          </button>
        )}

        {showControls && (
          <div style={{ display: 'grid', gap: 10 }}>
            {visibleBands.map((b, i) => (
              <Band key={b.label} label={b.label} narrow={narrow}>
                {b.ids.map((id) => control[id])}
                {i === lastBand && resetButton}
              </Band>
            ))}
            {visibleBands.length === 0 && (
              <Band label="Filters" narrow={narrow}>
                {resetButton}
              </Band>
            )}
          </div>
        )}

        {/* Scope line */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 7,
            marginTop: 11,
          }}
        >
          <span className="eyebrow">Scope</span>
          {applied === 0 && !carriedEmployee && <Pill>No filters</Pill>}
          {dirty && (
            <button type="button" style={{ ...linkBtn, fontSize: 'var(--t-table)' }} onClick={reset}>
              Clear all filters
            </button>
          )}

          {/*
            One pill per multi-select, naming the picks rather than counting them —
            "Claim type: Primary, Budget" is the scope; "Claim type: 2" is a riddle.
            Clearing the pill clears the whole control, which is the same promise the
            single-value pills already made.
          */}
          {MULTI_KEYS.filter((k) => filters[k].length > 0).map((k) => (
            <Pill key={k} onClear={() => clear(k)}>
              {FILTER_LABELS[k]}:{' '}
              {filters[k].length <= 3
                ? filters[k].join(', ')
                : `${filters[k].length} values`}
            </Pill>
          ))}

          {filters.supervisor !== ALL && (
            <Pill onClear={() => set('supervisor', ALL)}>{filters.supervisor}</Pill>
          )}
          {filters.employee !== ALL && (
            <Pill onClear={() => set('employee', ALL)}>{filters.employee}</Pill>
          )}
          {/* Employee is page-level here (EMPLOYEE_PAGE_SCOPED): one picked on another
              page is said, not applied, so it never cuts the teams down unseen. */}
          {carriedEmployee && (
            <span
              data-testid="carried-employee"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}
            >
              <Pill onClear={() => set('employee', ALL)}>Employee: {carriedEmployee} · not applied</Pill>
              <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
                Picked on another page. {PAGE_NAME[pathname] ?? 'This page'} shows everyone unless
                you pick an employee here.{' '}
                <button type="button" style={linkBtn} onClick={applyCarriedEmployee}>
                  Apply here
                </button>
              </span>
            </span>
          )}
          {filters.tenure !== ALL && (
            <Pill onClear={() => set('tenure', ALL)}>
              {TENURE_BANDS[Number(filters.tenure)]?.label}
            </Pill>
          )}
          {filters.payrollBand !== ALL && (
            <Pill onClear={() => set('payrollBand', ALL)}>
              Payroll {PAYROLL_BANDS[Number(filters.payrollBand)]?.label}
            </Pill>
          )}
          {/* The range had no pill, so a remembered one applied on every visit with
              nothing on the scope line to say so. */}
          {monthsSet && (
            <Pill onClear={() => setMonths(ALL, ALL)}>
              Months: {rangeSummary(derived.allMonths, filters.monthFrom, filters.monthTo, closedThrough)}
            </Pill>
          )}
          {filters.slice && (
            <Pill onClear={() => set('slice', null)}>Works {filters.slice.label}</Pill>
          )}
          {filters.employmentStatus !== EMPTY_FILTERS.employmentStatus && (
            <Pill onClear={() => clear('employmentStatus')}>
              {filters.employmentStatus === 'active' ? 'Active only' : 'Terminated only'}
            </Pill>
          )}
          {/* The Employee dropdown lists everybody, so it can pick someone the status
              control then hides — every page empty, with only this to say why. */}
          {pickedHidden && (
            <span style={{ fontSize: 'var(--t-micro)', color: 'var(--crit-ink)' }}>
              {filters.employee} is {pickedHidden.terminated ? 'terminated' : 'active'} and
              hidden by Employee status{' '}
              <button type="button" style={linkBtn} onClick={() => clear('employmentStatus')}>
                Show
              </button>
            </span>
          )}

          {/* The counts are pills too, per Shayna — they are part of the scope, not a
              footnote to it. */}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 7, alignItems: 'center' }}>
            <Pill>{int(scope.total)} people</Pill>
            <Pill>{int(scope.carrying)} carrying claims</Pill>
            {/* The scope row is the one part that never hides, so both switches live
                here and stay reachable. Narrow widths have the disclosure above. */}
            <FilterChooser
              hidden={hiddenFilters.filter((h) => (ALL_CONTROL_IDS as string[]).includes(h))}
              onChange={setHiddenFilters}
              filters={filters}
            />
            {!narrow && (
              <button
                type="button"
                className="btn"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                {filtersOpen ? 'Hide filters' : 'Show filters'}
              </button>
            )}
          </span>
        </div>

        {/* When a filter leaves something modelled, say what: the slice or the facility
            groups, and the claim filters, each in its own words. */}
        {scope.modeled &&
          [
            scope.modeledNote === SLICE_NOTE || scope.modeledNote === GROUP_NOTE ? scope.modeledNote : null,
            claimFilterNote === null
              ? null
              : scope.exact
                ? 'Every figure is split exactly by claim except weighted claims: the claim rules’ adjustment is split by each month’s billed share.'
                : 'Claims and claim labor are split exactly. Everything else is scaled by each person’s share — allocations, not counts.',
          ]
            .filter((n): n is string => n !== null)
            .map((n) => (
              <p
                key={n}
                className="mono"
                style={{
                  fontSize: 'var(--t-micro)',
                  color: 'var(--crit-ink)',
                  margin: '8px 0 0',
                }}
              >
                {n}
              </p>
            ))}
      </div>
    </div>
  )
}
